import type { MouseEventHandler, RefObject } from 'react';
import { useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';

import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useSharedState } from '@trello/shared-state';
import {
  useIsBoardPanelOpen,
  useIsPlannerPanelOpen,
} from '@trello/split-screen';

import type { CardType } from 'app/src/components/CardType';
import { useIsInboxBoard } from 'app/src/components/Inbox';
import { InboxModalContext } from 'app/src/components/Inbox/InboxModalContext';
import { inboxSelectedCardsSharedState } from 'app/src/components/Inbox/inboxSelectedCardsSharedState';

/**
 * Card types that can be opened via click or Enter keypress.
 * In the future, I think this can support all card types; right now,
 * however, board & link cards are rendered in both stacks, so it's not
 * trivial to refactor them. Once the old stack has been cleaned up, I
 * think we could expand this to support click handling for all types.
 */
export const CARD_TYPES_WITH_CARD_BACK = new Set<CardType>([
  'mirror',
  'default',
  'cover',
]);

interface UseCardFrontClickHandlerOptions {
  cardId: string;
  cardFrontRef: RefObject<HTMLElement>;
  cardType: CardType;
  cardUrl?: string;
  openCardBackDialog: (cardId: string) => Promise<void>;
}

/**
 * Handles click events on the card front container.
 * While an anchor tag is configured as the card name for accessibility,
 * we actually want the entire card front element to be clickable.
 */
export const useCardFrontClickHandler = ({
  cardId,
  cardFrontRef,
  cardType,
  cardUrl,
  openCardBackDialog,
}: UseCardFrontClickHandlerOptions) => {
  const isInboxBoard = useIsInboxBoard();
  const { value: isBulkActionEnabled } = useFeatureGate(
    'trello_web_inbox_bulk_actions',
  );
  const { value: isSplitScreenEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );
  const isBoardPanelOpen = useIsBoardPanelOpen();
  const isPlannerPanelOpen = useIsPlannerPanelOpen();
  const { modalId: isShowingModal, hideModal } = useContextSelector(
    InboxModalContext,
    (context) => context,
  );

  const [selectedCardIds, setSelectedCardIds] = useSharedState(
    inboxSelectedCardsSharedState,
  );

  return useCallback<MouseEventHandler>(
    (e) => {
      if (e.shiftKey && isBulkActionEnabled && isInboxBoard) {
        e.stopPropagation();
        e.preventDefault();
        const newSet = new Set(selectedCardIds);

        if (newSet.has(cardId)) {
          newSet.delete(cardId);
        } else {
          newSet.add(cardId);
        }
        setSelectedCardIds(newSet);
        return;
      }

      // Functionally just disables this callback in Storybook.
      if (!cardUrl) return;
      if (!CARD_TYPES_WITH_CARD_BACK.has(cardType)) return;
      if (!isInboxBoard && cardType === 'mirror') {
        return;
      }
      if (e.ctrlKey || e.shiftKey) {
        return;
      }
      // Ignore clicks on elements outside of the card front, e.g. popovers.
      if (!cardFrontRef.current?.contains(e.target as HTMLElement)) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      // Don't open card back of non-inbox or non-planner cards if board panel is closed
      if (
        isSplitScreenEnabled &&
        !isBoardPanelOpen &&
        !isInboxBoard &&
        !isPlannerPanelOpen
      ) {
        return;
      }

      if (isShowingModal) {
        hideModal();
      }

      if (e.metaKey) {
        Analytics.sendClickedButtonEvent({
          buttonName: 'cardFrontNewTab',
          source: 'boardScreen',
        });
        window.open(cardUrl, '_blank');
        return;
      }

      openCardBackDialog(cardId);
    },
    [
      isBulkActionEnabled,
      isInboxBoard,
      cardUrl,
      cardType,
      cardFrontRef,
      isSplitScreenEnabled,
      isBoardPanelOpen,
      isPlannerPanelOpen,
      isShowingModal,
      openCardBackDialog,
      cardId,
      selectedCardIds,
      setSelectedCardIds,
      hideModal,
    ],
  );
};
